import { CommonDialogs, ImageTable } from "@mcleod/common";
import { BlurEvent, ChangeEvent, ClickEvent, DataSource, DataSourceAction, DataSourceMode, DataSourceModeChangeEvent, LookupModelSearchEvent, LookupModelSelectionEvent, Textbox } from "@mcleod/components";
import { Api, ArrayUtil, CurrencySettings, McLeodClassicIntegration, ModelRow, Navigation, StringUtil, CurrencyUtil } from "@mcleod/core";
import { getDispatchControlBoolean, getDispatchControlValue, ResponsibilityUtil, UsersResponsibilitySlideoutMF } from "@mcleod/general";
import { DuplicateId, DuplicateIdProps } from "../DuplicateId";
import { OrderTabView } from "../OrderTabView";
import { AutogenLayoutTrailer } from "./autogen/AutogenLayoutTrailer";
import { EquipmentAssignments } from "./EquipmentAssignments";
import { EquipmentItem } from "./EquipmentGroupValidation";
import { Seating, SeatingEquipmentType } from "./Seating";

const viewTrailerURL = "lme/dispatch/asset/Trailer?mode=update&key=";
const DUPLICATE_VALIDATION_PROPS: DuplicateIdProps = {
    tableName: "trailer",
    screenName: "Trailer",
    navigateToUrl: viewTrailerURL
}

export class Trailer extends AutogenLayoutTrailer {
    _trailerSharing: boolean = getDispatchControlBoolean("trailer_sharing");
    buttonOpenClassic: any;

    override onLoad() {
        CurrencyUtil.configureCurrencySelector(this.textboxValueCurrencyType);
        this.layoutTableComment.displayCopyOrderFields = false;
        this.layoutEquipmentIssued.setParentRowType(EquipmentItem.TRAILER);
        this.sourceTrailer.afterGetDataboundValues = (row: ModelRow, dataSource: DataSource) => this.sourceTrailerAfterGetDataboundValues(row, dataSource);

        DuplicateId.configureIdTextbox(this.textId, DUPLICATE_VALIDATION_PROPS);

        this.setupOrderIdLabel();
        this.sourceTrailer.addAfterModeChangeListener(async () => {
            if (this.sourceTrailer.mode == DataSourceMode.SEARCH) {
                this.buttonTrailerUsers.visible = await ResponsibilityUtil.isFilterEnabled("trailer_filter");
            }
            if (this.sourceTrailer.mode == DataSourceMode.ADD) {
                this.buttonTrailerUsers.visible = false;
            }
            if (this.sourceTrailer.mode == DataSourceMode.UPDATE) {
                const controlValue = this.mainDataSource.activeRow.getString("trailer_filter_enabled");
                this.buttonTrailerUsers.visible = controlValue === "Y";
            }
        });

        this.textboxReeferId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            this.reeferIdModelSelection(event);
        });

        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            this.textboxValueCurrencyType.defaultDataValue = CurrencySettings.getFunctionalCurrency();
            this.mainDataSource.activeRow?.get("dollar_value")?.setCurrencyCode(CurrencySettings.getFunctionalCurrency());
            this.textboxValueCurrencyType.visible = true;
            this.textboxDollarValue.nextFocusable = this.textboxValueCurrencyType.id;
        }
    }

    private async reeferIdModelSelection(event: LookupModelSelectionEvent) {
        if (this.mainDataSource.mode != DataSourceMode.SEARCH && event.hasSelection() === true) {
            const response = await Api.search("lme/dispatch/mobilecomm/get-update-trailer-reefer", {
                "reefer": event.selection?.get("id"),
                "excluded_trailer": this.mainDataSource.activeRow?.get("id")
            });

            if (response.data[0]?.trailer_list === undefined) {
                this.mainDataSource.activeRow.set("sync_trailers", false);
                return;
            }

            const userConfirmed = await this.confirmChangeTrailer(response.data[0].trailer_list);
            this.mainDataSource.activeRow.set("sync_trailers", userConfirmed);
            if (!userConfirmed) {
                const origRow = this.mainDataSource.activeRow.originalData;
                this.textboxReeferId.text = origRow["reefer_id"];
                this.textboxReeferId.focus();
            }
        }
    }

    private async confirmChangeTrailer(defaultTrailerList: any): Promise<boolean> {
        return await CommonDialogs.showYesNo(
            `Only one reefer can be associated with a trailer, selecting "Yes" will remove the selected reefer from trailer(s): ${this.getTrailerList(defaultTrailerList)}`,
            "Change Trailer Reefer"
        );
    }

    private getTrailerList(default_trailer_list: any): string {
        let result = "\n\n";
        for (let i = 0; i < default_trailer_list.length; i++) {
            result += default_trailer_list[i].trailer_id + "\n";
        }
        return result;
    }

    private getTrailerUsersAgencyField(): string {
        return "primary_agency";
    }

    /** This is an event handler for the beforeExecution event of sourceTrailer.  It's gross. */
    sourceTrailerAfterGetDataboundValues(row: ModelRow, dataSource: DataSource) {
        if (dataSource.mode === DataSourceMode.SEARCH) {
            //have to set primary_agency in search mode because the field isn't bound
            const primaryAgencyField = this.getTrailerUsersAgencyField();
            row.set(primaryAgencyField, dataSource.searchRow.get(primaryAgencyField));

            //have to add agency_link search data to the filter manually, since there are no bound components for those records
            if (ArrayUtil.isEmptyArray(this.sourceAgencyLink.data))
                return;
            row.addLinkedModel({ model: this.sourceAgencyLink.url, rows: [this.sourceAgencyLink.data[0]] });
        }
    }

    textboxTrailerGroupBeforeLookupModelSearch(event) {
        event.filter.is_trailer = 'Y';
    }

    buttonOpenClassicOnClick(event: ClickEvent) {
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.sf.EntryTrailer", this.mainDataSource.activeRow.get("id"));
    }

    /** This is an event handler for the onClick event of labelPreassignment. */
    labelPreassignmentOnClick(event: ClickEvent) {
        const dispatchBy = getDispatchControlValue("dispatch_by");
        const equipmentId: string = dispatchBy == "T" ? this.activeRow.get("tractor.id") : this.activeRow.get("driver.id");
        EquipmentAssignments.showSlideout({
            equipmentId: equipmentId,
            tractorId: this.activeRow.get("tractor.id"),
            movementId: this.activeRow.get("movement.id")
        });
    }

    sourceTrailerAfterExecution(event) {
        if (event?.getAction() == DataSourceAction.SEARCH || event?.getAction() == DataSourceAction.UPDATE) {
            if (this.activeRow?.isNull("movement.id")) {
                this.labelOrder.visible = false;
                this.textDriverId.visible = false;
                this.textTractorId.visible = false;
                this.textboxLastLocation.visible = false;
                this.textboxDriverPtaDate.visible = false;
                this.panelAdditionalInfo.visible = false;
                this.layoutCurrentDispatchStops.visible = false;
                this.textboxDriverLastHomeDate.visible = false;
                this.textboxDriverNextHomeDate.visible = false;
                this.panelDriver2.visible = false;
                this.labelNoDispatchInfo.visible = true;
            } else {
                this.layoutCurrentDispatchStops.mainDataSource.search({ id: this.activeRow?.get("movement.id") });
            }

            (this.layoutImageTable as ImageTable).populateForRow("E", this.activeRow?.get("id"));
        }

        if (event?.getAction() == DataSourceAction.UPDATE) {
            this.textId.enabled = false;
            this.textId.required = true;
            this.textDriverId.printable = true;
            this.textTractorId.printable = true;
            this.textboxLastLocation.printable = true;
            this.textboxDriverPtaDate.printable = true;
        }

        if (event.getAction() == DataSourceAction.ADD) {
            this.tabDispatch.visible = false;
            this.textId.enabled = false;
        }

        this.syncButtonLocateTrailerState(this.activeRow);

        if (!event.data?.isNull("dolly1.id") && !event.data?.isNull("dolly2.id")) {
            this.textDolly1Id.caption = "Dolly 1";
        }

        if (!event.data?.isNull("trailer2.id") || !event.data?.isNull("trailer3.id")) {
            this.textTrailer1Id.caption = "Trailer 1";
        }
    }

    sourceTrailerBeforeModeChange(event) {
        this.buttonOpenClassic.visible = event.newMode === DataSourceMode.UPDATE;
        this.tabDispatch.visible = event.newMode !== DataSourceMode.ADD;

        if (event.newMode === DataSourceMode.UPDATE) {
            this.textId.required = true;
            this.panelOrder.visible = true;
        }

        if (event.newMode === DataSourceMode.SEARCH) {
            this.textboxLastLocation.visible = false;
            this.panelAdditionalInfo.visible = false;
            this.panelCurrentDispatchSearch.visible = true;
            this.layoutCurrentDispatchStops.visible = false;
        }
    }

    private setupOrderIdLabel() {
        this.labelOrderId.addUnderlineWhenMouseOver();
    }

    public static navigateTo(id: string, newTab = true): void {
        Navigation.navigateTo(viewTrailerURL + id, { newTab: newTab });
    }

    textboxRoundToOneDecimalOnBlur(event: BlurEvent) {
        if (this.mainDataSource?.mode === DataSourceMode.ADD
            || this.mainDataSource?.mode === DataSourceMode.UPDATE
            || this.mainDataSource?.mode === DataSourceMode.NONE) {
            const textbox = event.target as Textbox;
            const textNumber = Number(textbox.text);
            if (!Number.isNaN(textNumber)) {
                textbox.text = (Math.round(textNumber * 10) / 10).toFixed(1);
            }
        }
    }

    sourceTrailerAfterModeChange(event: DataSourceModeChangeEvent) {
        this.tabImage.visible = this.tabDispatch.visible = event.newMode == DataSourceMode.UPDATE;
        this.buttonLocateTrailer.visible = event.newMode == DataSourceMode.UPDATE;
        this.textboxMaxTemp.nextFocusable = event.newMode == DataSourceMode.SEARCH
            ? this.textboxTrailerBatteryStatus.id : this.textboxTrailerType.id;
    }

    /** This is an event handler for the onClick event of labelOrderId. */
    labelOrderIdOnClick(event: ClickEvent) {
        const orderId = this.activeRow?.get("movement_order.order_id");
        OrderTabView.navigateTo({ key: orderId, newTab: true })
    }

    /** This is an event handler for the onClick event of buttonTrailerUsers. */
    buttonTrailerUsersOnClick(event: ClickEvent) {
        const urs = UsersResponsibilitySlideoutMF.builder()
                .layoutName("lme/dispatch/asset/TrailerUsers")
                .title(this.getTrailerUsersSlideoutTitle())
                .idFilter(this.getTrailerUsersIdFilter())
                .parentMainDataSource(this.sourceTrailer)
                .parentAgencyLinkDataSource(this.sourceAgencyLink)
                .primaryAgencyField(this.getTrailerUsersAgencyField())
                .build();
        urs.show();
    }

    aceTypeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.version_input = "004060USCBP";
        event.filter.element_input = "40";
    }

    private getTrailerUsersSlideoutTitle(): string {
        const name = this.sourceTrailer.activeRow?.get("id");
        return "Trailer Users" + ((StringUtil.isEmptyString(name) === false) ? (" - " + name) : "");
    }

    private getTrailerUsersIdFilter(): any {
        const id = this.sourceTrailer.activeRow?.get("id");
        if (id != null)
            return { id: id };
        return null;
    }

    private buttonLocateTrailerOnClick(event: ClickEvent) {
        const id = this.activeRow?.get("id");
        Seating.showSlideout({
            equipmentId: id,
            equipmentType: SeatingEquipmentType.TRAILER,
            doAfterSave: () => Trailer.navigateTo(id, false)
        });
    }

    private syncButtonLocateTrailerState(modelRow: ModelRow) {
        if (modelRow?.getString("is_active") != "A") {
            this.buttonLocateTrailer.disabledTooltip = "Trailer's Service Status must be Active to locate.";
            this.buttonLocateTrailer.enabled = false;
            return;
        }
        if (modelRow?.get("movement.status") === "P") {
            this.buttonLocateTrailer.disabledTooltip = "Cannot locate Trailer for In-Progress movements";
            this.buttonLocateTrailer.enabled = false;
            return;
        }
        this.buttonLocateTrailer.enabled = true;
    }

    /** This is an event handler for the onBlur event of textboxDollarValue. */
    textboxDollarValueOnBlur(event: BlurEvent) {
        const currencySettings = CurrencySettings.getSingleton();
        if (event.changedWhileFocused && (this.mainDataSource.mode === DataSourceMode.ADD || this.mainDataSource.mode === DataSourceMode.UPDATE)
            && currencySettings.multiCurrencyEnabled()) {
            const currencyType = this.mainDataSource.activeRow?.get("dollar_value_c", currencySettings.getFunctionalCurrency());
            this.setMultiCurrencyValue(currencyType);
        }
    }

    /** This is an event handler for the onChange event of textboxValueCurrencyType. */
    textboxValueCurrencyTypeOnChange(event: ChangeEvent) {
        const currencyType = this.textboxValueCurrencyType.getDataValue();
        this.setMultiCurrencyValue(currencyType);
    }

    private setMultiCurrencyValue(currencyType: string) {
        if (this.mainDataSource.mode === DataSourceMode.ADD || this.mainDataSource.mode === DataSourceMode.UPDATE) {
            const dollarValue = this.mainDataSource.activeRow?.getCurrency("dollar_value", null);
            if (currencyType && dollarValue != null) {
                dollarValue.setCurrencyCode(currencyType);
                this.mainDataSource.activeRow?.set("dollar_value", dollarValue);
            }
        }
    }
}
